import { CheckCircle } from 'lucide-react';
import { OptionCard } from './OptionCard';

/** How a new user is brought on: emailed an invite link, or created with a password now. */
export type UserCreationAction = 'create' | 'invite';

/**
 * "Send an invitation" vs "Create the account" toggle — shared by every
 * user-creation modal (admin Users, reseller Staff, owner Team, reseller
 * CustomerDetail). Feature #37. Renders at the top of the form; the password
 * field + submit wiring live in each modal.
 */
export function CreateOrInviteToggle({
  action,
  onChange,
}: {
  action: UserCreationAction;
  onChange: (a: UserCreationAction) => void;
}) {
  return (
    <div className="space-y-2">
      <OptionCard selected={action === 'invite'} onSelect={() => onChange('invite')} asLabel={false}>
        <div className="font-medium text-sm text-zinc-950 dark:text-zinc-50">Send an invitation</div>
        <div className="text-xs text-zinc-600 dark:text-zinc-300">Email a secure link; they set their own password.</div>
      </OptionCard>
      <OptionCard selected={action === 'create'} onSelect={() => onChange('create')} asLabel={false}>
        <div className="font-medium text-sm text-zinc-950 dark:text-zinc-50">Create the account</div>
        <div className="text-xs text-zinc-600 dark:text-zinc-300">Set a password now; they can sign in immediately.</div>
      </OptionCard>
    </div>
  );
}

/**
 * Post-create success panel (the "create" counterpart to InviteLinkResult, which
 * is shown after an invite). Confirms the account is ready to sign in.
 */
export function AccountCreatedPanel({ email }: { email: string }) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10 px-4 py-3">
      <CheckCircle className="w-4 h-4 mt-0.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
      <div className="text-sm text-emerald-900 dark:text-emerald-200">
        Account created for <span className="font-medium">{email}</span>. They can sign in now.
      </div>
    </div>
  );
}
